import { useQuery } from '@tanstack/react-query';
import { getAttachments, type AttachmentEntityType, type AttachmentListItem } from '@/api/attachments';
import { AttachmentItem } from './AttachmentItem';
import { AttachmentUploader } from './AttachmentUploader';

interface AttachmentsSectionProps {
  entityType: AttachmentEntityType;
  entityId: number;
  /** True when the caller may upload/delete files (oscar-admin / oscar-helpdesk). */
  canWrite: boolean;
}

/**
 * Reusable attachments panel. Lists files most-recent-first and, for writers, a
 * drag-drop uploader below. Read-only viewers can download but not upload.
 * Mounted in the contract detail page's Files tab.
 */
export function AttachmentsSection({ entityType, entityId, canWrite }: AttachmentsSectionProps) {
  const { data, isPending, isError } = useQuery<AttachmentListItem[]>({
    queryKey: ['attachments', { entityType, entityId }],
    queryFn: ({ signal }) => getAttachments(entityType, entityId, signal),
  });

  return (
    <section className="space-y-3">
      {isPending && <p className="text-sm text-darkGrey/60">Loading files…</p>}
      {isError && <p className="text-sm text-red" role="alert">Couldn’t load files, please reload.</p>}

      {!isPending && !isError && (
        <>
          {data!.length === 0 ? (
            <p className="text-sm text-darkGrey/60">No files yet.</p>
          ) : (
            <ul className="space-y-2">
              {data!.map((a) => (
                <li key={a.AttachmentId}>
                  <AttachmentItem attachment={a} entityType={entityType} entityId={entityId} />
                </li>
              ))}
            </ul>
          )}

          {canWrite && <AttachmentUploader entityType={entityType} entityId={entityId} />}
        </>
      )}
    </section>
  );
}

export default AttachmentsSection;
